import moment from "moment";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { CalendarLengthButton, CalendarLengthButtonGroup, ScheduleHeaderContainer, ScheduleHeaderButtons, ScheduleTitle, SearchButton, ScheduleKeywordSearchWrapper, CollapsibleFiltersWrapper, FilterToggleBar } from "./styles";
import { AppIconButton } from "@components";
import { ArrowLeftIcon, ArrowRightIcon, SearchIcon } from "@assets";
import "moment/locale/ja";
import { selectAuth, selectPreloadData, useAppSelector, useAppDispatch, fetchReservations, selectReservations, selectReservationsLoading, fetchRooms, selectRooms, fetchTypes, selectTypes, updateReservation, selectLists, createList, fetchLists, deleteList, updateList } from "@redux";
import { Dialog, DialogContent, Grid2 } from "@mui/material";
import { useHistory } from "react-router-dom";
import { CalendarGroupingEnum, CalendarSpanEnum, ReservationsParticipant } from "src/types";
import { Box, Button, Grid, IconButton } from '@mui/material';
import { ScheduleCalendar } from "./ScheduleCalendar";
import { AppCustomeSelect, AppInput } from '../Common';
import scStyled from 'styled-components';
import { useDynamicPadding  } from "./FrontEndHelpers";
import { ParticipantsSelector } from "./ParticipantsSelector";
import { PassChangeButton } from '../Profile';
import { CircularProgress } from "@mui/material";
import { useSortedGroupList } from "src/Utils/Hooks/useSorter";
import { createScheduleEvents } from "./createScheduleEvents";
import { holidayApi } from "src/Api/holiday";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";
import { useScheduleRoomSelectionHistories } from "src/Utils/Hooks/useScheduleRoomSelectionHistories";
import { useDefaultParticipantsSelectionHistories } from "src/Utils/Hooks/useDefaultParticipantSelectionHistories";
import { useAppNavigation } from "@utils";

export const ScheduleView = ({ contentDisplayed = "reservations" }: { contentDisplayed?: "reservations" | "facilities" }) => {
  const reservationsDisplayed = contentDisplayed === "reservations";
  const facilitiesDisplayed = contentDisplayed === "facilities";

  //ページに応じてholidaysを取得する
  const [scheduleHolidays, setScheduleHolidays] = useState<string[]>([]);
  const [facilitiesHolidays, setFacilitiesHolidays] = useState<string[]>([]);

  //ユーザーとグループの履歴保存付きリスト用
  const [userOptions, setUserOptions] = useState<{value: string, label: string, type: string}[]>([]);
  const [groupOptions, setGroupOptions] = useState<{value: string, label: string, type: string}[]>([]);

  // 施設予約初期読み込み用
  const [firstRender, setFirstRender] = useState(true);

  const dispatch = useAppDispatch();
  const history = useHistory();
  const { navigateWithState, getQueryParam, currentPath, currentQueryString } = useAppNavigation();

  // Get user ID from auth state
  const user = useAppSelector(selectAuth);
  const userId = user.userInfo?.id;
  
  // Get holidays from preload data
  const preloadData = useAppSelector(selectPreloadData);
  // const holidays = preloadData.holidays;

  //holidaysをAdminの設定に応じて取得する値を変更
  const months = useMemo(() => {
    const list: { year: number; month: number }[] = [];
    const base = moment();
    for (let offset = -3; offset <= 3; offset++) {
      const target = base.clone().add(offset, "months");
      list.push({ year: target.year(), month: target.month() + 1 });
    }
    return list;
  }, []);

  const holidays = facilitiesDisplayed ? facilitiesHolidays : scheduleHolidays;

  useEffect(() => {
    if (contentDisplayed === "facilities") {
    fetchAllHolidays("facilities");
  } else if (contentDisplayed === "reservations") {
    fetchAllHolidays("schedule");
  }
  }, [contentDisplayed]);

  const fetchAllHolidays = async (pageKey: "schedule" | "facilities") => {
    try {
      const results = await Promise.all(
        months.map(({ year, month }) =>
          holidayApi.getMonthHolidays({ year, month, page_key: pageKey })
        )
      );
      const holidayDates = results.flatMap(res =>
        res.data.data.date_holiday.map((d) => d.date)
      );
      const uniqueDates = Array.from(new Set(holidayDates));

      if (pageKey === "schedule") {
        setScheduleHolidays(uniqueDates);
      } else {
        setFacilitiesHolidays(uniqueDates);
      }
    } catch (error) {
      console.error("複数月の休日情報取得失敗", error);
    }
  };


  // Get all users and sort them
  const users = preloadData.users;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(userId || null);

  const { list, fetchList, storeSelection } = useUserSelectionHistories(userId ?? 0);
  const { defaultParticipantsHistory, fetchDefaultParticipantsHistory, storeDefaultParticipantsSelection } = useDefaultParticipantsSelectionHistories(userId ?? 0);
  const firstRenderRef = useRef(true);

  // Get groups and sort them
  const groups = preloadData.groups;
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const sortedGroups = useSortedGroupList(groups, false);

  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(userId ?? 0);


  //期間、ユーザー関係なしに全データロードなのでパフォーマンス要注意。
  const reservations = useAppSelector(selectReservations);

  const rooms = useAppSelector(selectRooms);
  const reservationsLoading = useAppSelector(selectReservationsLoading);
  const types = useAppSelector(selectTypes);

  // 予定タイプ選択プルダウン用
  const [selectedType, setSelectedType] = useState<{value: number, label: string} | null>(null);

  // キーワード検索用（入力と決定）
  const [keywordInput, setKeywordInput] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 施設選択プルダウン用
  const [roomOptions, setRoomOptions] = useState<{value: number, label: string}[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<{value: number, label: string} | null>(null);
  const { scheduleRoomList, fetchScheduleRoomList, storeScheduleRoomSelection, loaded } = useScheduleRoomSelectionHistories(userId ?? 0);

  // 公開非公開切り替え用
  const [privateSelected, setprivateSelected] = useState<"private" | "public" | "all">("public");

  // Get lists (favorites and default participants) from slice
  const lists = useAppSelector(selectLists).data.filter(list => !list.deleted_at && list.creator_id === userId);
  // console.log("All lists:", lists);
  const [defaultListValue, setDefaultListValue] = useState<{value: number, label: string} | null>(null);
  const [favoriteListValue, setFavoriteListValue] = useState<{value: number, label: string} | null>(null);

  const [calendarSpan, setCalendarSDpan] = useState<CalendarSpanEnum>(reservationsDisplayed ? 'month' : 'week');
  const [calendarGrouping, setCalendarGrouping] = useState<CalendarGroupingEnum>(reservationsDisplayed ? 'individual' : 'facilities');
  const [currentDate, setCurrentDate] = useState(new Date());

  const stackedView = (calendarGrouping !== 'individual') && (calendarSpan === 'week');
  const columnsView = (calendarGrouping !== 'individual') && (calendarSpan === 'day');

  // If the URL has eventId and optional date parameters, open the event request modal
  const [openEventRequest, setOpenEventRequest] = useState<null | {reservationId: number; date?: string;}>(null);

  useEffect(() => {
    const eventIdParam = getQueryParam("eventId");
    const dateParam = getQueryParam("date");

    if (!eventIdParam) {
      setOpenEventRequest(null);
      return;
    }

    const reservationId = Number(eventIdParam);
    if (Number.isNaN(reservationId) || reservationId <= 0) {
      console.log("[Schedule] invalid eventId:", eventIdParam);
      setOpenEventRequest(null);
      return;
    }

    // Optional: if a date is provided, navigate calendar there so the occurrence is visible
    if (dateParam && moment(dateParam, "YYYY-MM-DD", true).isValid()) {
      setCurrentDate(moment(dateParam, "YYYY-MM-DD").toDate());
    }

    setOpenEventRequest({
      reservationId: reservationId,
      date: dateParam && moment(dateParam, "YYYY-MM-DD", true).isValid() ? dateParam : undefined,
    });
  }, [currentQueryString, getQueryParam]);


  // Modal state for favorite/default users
  const [userSelectionType, setUserSelectionType] = useState<null | 'favorite' | 'default'>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ReservationsParticipant[]>([]);
  const [favoriteUserIds, setFavoriteUserIds] = useState<number[]>([]);
  const [groupUserIds, setGroupUserIds] = useState<number[]>([]);
  const [defaultParticipantUserIds, setDefaultParticipantUserIds] = useState<number[]>([]);

  const [calendarRoomIds, setCalendarRoomIds] = useState<number[]>([]);

  const calendarUserIds = [userId, ...groupUserIds];

  const groupsUsers = preloadData.groupsUsers;

  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  const headerDummyRef = useRef<HTMLDivElement>(null);
  const headerDummyIndividualWeeklyRef = useRef<HTMLDivElement>(null);

  // Create events from the reservations
  const events = createScheduleEvents(reservations.data, currentDate, types.data);
  //console.log("Created events:", events);

  // handler to be called by ScheduleCalendar when navigation is needed
  const handleNavigate = (newDate: Date, newView: CalendarSpanEnum) => {
    setCurrentDate(newDate);
    setCalendarSDpan(newView);
    if (newView === 'week') { 
        calculateCurrentWeekRow(newDate);
    }
  };

  // Keep the "isMobile" variable in sync with the window size
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Loading overlay NOT NEEDED but keeping just in case
//   const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
//   useEffect(() => {
  
//   let hideTimer: NodeJS.Timeout;

//   if (reservationsLoading) {
//     // Show overlay immediately when loading starts
//     setShowLoadingOverlay(true);
//   } else if (showLoadingOverlay) {
//     // Add a minimum display time when loading finishes
//     hideTimer = setTimeout(() => {
//       setShowLoadingOverlay(false);
//     }, 300); // 300ms delay before hiding
//   }

//   return () => {
    
//     clearTimeout(hideTimer);
//   };
// }, [reservationsLoading, showLoadingOverlay]);
  

  useEffect(() => {
      const el = headerDummyRef.current;
      if (!el) return;
  
      const isHeaderDummyActiveByCalendar = calendarGrouping !== 'individual' && calendarSpan === 'week';
  
      if (isHeaderDummyActiveByCalendar) {
     
          el.style.display = "block";
  
      } else {
        el.style.display = "none";
      }
  
    }, [calendarSpan, calendarGrouping, currentDate]);
  
  
   useEffect(() => {
    const el = headerDummyIndividualWeeklyRef.current;
    if (!el) return;
  
    const isHeaderDummyActiveByCalendar = calendarGrouping === 'individual' && calendarSpan === 'week';
  
    // Find the scrollable container and the .rbc-row-content element
    const scrollContainer = document.querySelector('#allCalendarElements') as HTMLElement | null;
    const rowContent = document.querySelector('.rbc-row-content') as HTMLElement | null;
  
    if (!isHeaderDummyActiveByCalendar || !rowContent || !scrollContainer) {
      el.style.display = "none";
      el.style.height = '0px';
      return;
    }
  
    el.style.display = "block";
  
    // Function to update visible height
    const updateHeight = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const rowRect = rowContent.getBoundingClientRect();
  
      // Calculate intersection
      const visibleTop = Math.max(containerRect.top, rowRect.top);
      const visibleBottom = Math.min(containerRect.bottom, rowRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  
      el.style.height = `${visibleHeight + 26}px`;
    };
  
    // Initial update
    updateHeight();
  
    // Listen for scroll and resize
    scrollContainer.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);
  
    // Clean up
    return () => {
      scrollContainer.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, [calendarSpan, calendarGrouping, currentDate, selectedUserId]);


  useEffect(() => {  
    dispatch(fetchRooms());
    dispatch(fetchTypes());
    dispatch(fetchReservations());
    //refreshReservations();
    dispatch(fetchLists());
  }, [dispatch, currentDate]);

  // We can't fetch reservations with a date range, for the following reason:
  // Imagine we're making a recurring reservation that is weekly and lasts for a couple years.
  // Without information about all reservations for the next couple years, we can't know if the reservation we're making overlaps with existing ones.
  // Adding start date might be ok for this, but even if the only conflicts could exist in the past, the client might still dislike that.
  const refreshReservations = () => {
    dispatch(fetchReservations());

//    // calculate days that appear outside current month
//    const bufferDays = 14; 
//    
//    const fetchStartDate = moment(currentDate)
//      .startOf('month')
//      .subtract(bufferDays, 'days')
//      .format('YYYY-MM-DD');
//      
//    const fetchEndDate = moment(currentDate)
//      .endOf('month')
//      .add(bufferDays, 'days')
//      .format('YYYY-MM-DD');
//    
//    console.log('Fetching reservations from', fetchStartDate, 'to', fetchEndDate);
//    console.log('Fetching reservations from', fetchStartDate, 'to', fetchEndDate);
//
//    dispatch(fetchReservations({
//      start_date: fetchStartDate,
//      end_date: fetchEndDate
//    }));
  };

  // Update the list of rooms when the rooms data changes
  useEffect(() => {
    if (rooms?.data) {
      // Get all room IDs from the rooms data
      const roomIds = rooms.data
      .filter(room => !room.deleted_at) // Filter out deleted rooms
      .map(room => room.id);
      setCalendarRoomIds(roomIds);
    }
  }, [rooms]);

  // 施設選択プルダウンの初期化と更新、選択施設の変更に応じてカレンダーの施設IDを更新
  useEffect(()=>{
    if (!scheduleRoomList || scheduleRoomList.length === 0) return;

    if (!selectedRoomId) {
      setCalendarRoomIds(scheduleRoomList.map(r => r.id));
    } else {
      setCalendarRoomIds([selectedRoomId.value]);
    }

    if (scheduleRoomList) {
        
      setRoomOptions(scheduleRoomList.map(room => ({
        value: room.id,
        label: room.name
      })));
  
    }

  }, [selectedRoomId, scheduleRoomList]);

  useEffect(() => {
    // Give browser a moment to render the calendar
    setTimeout(() => {
      const monthViews = document.querySelectorAll('.rbc-month-view');
      monthViews.forEach(monthView => {
        monthView?.classList.add('rbc-month-view-short');
        if (calendarSpan === 'week' && (calendarGrouping === 'group' || calendarGrouping === 'favorites' || calendarGrouping === 'facilities')) {
          calculateCurrentWeekRow(currentDate);
        } else {
          monthView?.classList.remove('rbc-month-view-short');
          // Remove current week class from all rows
          const monthRows = document.querySelectorAll('.rbc-month-view .rbc-month-row');
          monthRows.forEach(row => {
            row.classList.remove('current-week');
          });
        }
      });
    }, 100);
  }, [calendarSpan, calendarGrouping, currentDate, groupUserIds, events, reservations.data, rooms?.data]);


  useEffect(() => {
    if ((calendarGrouping === 'group' || calendarGrouping === 'favorites') && calendarSpan === "month") {
      setCalendarSDpan('week');
    }
  }, [calendarGrouping, calendarSpan]);

  useEffect(() => {
    if (userId && calendarGrouping !== "individual") {
      setSelectedUserId(userId);
    }
  }, [calendarGrouping, userId]);

  // Load favorite and default users from slice
  useEffect(() => {
    // Use the currently selected favorite list instead of just finding the first one
    const favoriteList = lists.find(list => 
      list.type === 'favorite' && 
      list.id === favoriteListValue?.value
    );
    const defaultList = lists.find(list => list.type === 'default');

    if (favoriteList) {
      setFavoriteUserIds(favoriteList.participants);
    }
    if (defaultList) {
      setDefaultParticipantUserIds(defaultList.participants);
    }
  }, [lists, favoriteListValue]);

  useEffect(() => {
    if (userSelectionType === 'default') {

      const savedUserIds = defaultParticipantUserIds;
      const savedUsers = users.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      const otherUsers = users.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});

      //setSelectedUsers(savedUsers);
      //setAvailableUsers(otherUsers);
    }
    if (userSelectionType === 'favorite') {
      const savedUserIds = favoriteUserIds;
      const savedUsers = users.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      const otherUsers = users.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      
      //setSelectedUsers(savedUsers);
      //setAvailableUsers(otherUsers);
    }

    // Good to clear the list, so if the user had some selected user in one screen, then moves to another, the old selections will be cleared
    if (userSelectionType === null) {
      setSelectedUsers([]);
      setAvailableUsers(users);
    }
  }, [userSelectionType, favoriteUserIds, defaultParticipantUserIds, users, userId]);

  //グループの初期選択の値は別途指定するためここではコメントアウト
  // useEffect(() => {
  //   if(sortedGroups && selectedGroupId === null) {
  //     // If no group is selected, set the first group as selected
  //     const firstGroup = sortedGroups[0];
  //     if (firstGroup && firstGroup.id) {
  //       setSelectedGroupId(firstGroup.id);
  //     }
  //   }
  // }, [sortedGroups, selectedGroupId]);    

  useEffect(() => {
    if (calendarGrouping === 'favorites') {
      // console.log("favoriteListValue:", favoriteListValue);
      const favoriteLists = lists.filter(list => list.type === 'favorite');

      // Early return if no favorite lists exist at all
      if (!favoriteLists || favoriteLists?.length === 0) {
        // console.log("No favorite lists found, using empty array");
        setGroupUserIds([]);
        return;
      }

      // If user has favorite lists but none is selected, select the first one
      if (!favoriteListValue && favoriteLists.length > 0) {
        const firstFavoriteList = favoriteLists[0];
        setFavoriteListValue({
          value: firstFavoriteList.id,
          label: firstFavoriteList.name || 'お気に入りリスト'
        });
        return;
      }

      // Early return if favoriteListValue is null but favorite lists exist
      if (!favoriteListValue) {
        // console.log("No favorite list selected, using empty array");
        setGroupUserIds([]);
        return;
      }
    
      const favoriteList = lists.find(list => 
        list.type === 'favorite' && 
        list.id === favoriteListValue.value
      );
      // console.log("Filtered user IDs for faves:", favoriteList?.participants);
      setGroupUserIds(favoriteList ? favoriteList.participants : []);
    }
  }, [calendarGrouping, favoriteListValue?.value, lists.length]); 

  useEffect(() => {
    if (calendarGrouping === 'group') {
      if(selectedGroupId) {
        const filteredUserIds: number[] = Array.from(new Set(
          preloadData.groupsUsers
            .filter((gu) => gu.group_id === selectedGroupId)
            .filter((gu) => gu.user_id !== userId) // Exclude the current user
            .map((gu) => gu.user_id)
        ));
        console.log("Filtered user IDs for group:", filteredUserIds);
        setGroupUserIds(filteredUserIds);
      } else {
        // Prevent unintentional clearing on partial state - This used to cause the calendar to clear if navigation was done too soon
        // setGroupUserIds([]);
        return;
      }
    }
  }, [calendarGrouping, favoriteListValue, selectedGroupId]); 

  const calculateCurrentWeekRow = (weekDate: Date = currentDate) => {
    const needleDate = weekDate.getDate();
    const monthViews = document.querySelectorAll('.rbc-month-view');

    monthViews.forEach(monthView => {
      const monthRows = monthView.querySelectorAll('.rbc-month-view .rbc-month-row');
    
      monthRows.forEach((row, index) => {
        const disqualified = (needleDate > 20 && index === 0) || (needleDate < 10 && index > 3);
        // Find date cells in this row
        const dateCells = row.querySelectorAll('.rbc-date-cell');
        
        // Check if any date in this row is from the current week
        const isCurrentWeek = Array.from(dateCells).some(cell => {
          const cellText = cell.textContent || '';
          const cellDate = parseInt(cellText, 10);
          
          if (isNaN(cellDate)) return false;
          
          // Create a date for this cell
          const cellDateObj = new Date(currentDate);
          cellDateObj.setDate(cellDate);
          
          // Check if this date is in the same week as today
          const todayWeekStartDate = moment(weekDate).startOf('week').toDate();
          const todayWeekEndDate = moment(weekDate).endOf('week').toDate();
          
          return cellDateObj >= todayWeekStartDate && cellDateObj <= todayWeekEndDate;
        });
        
        // Add/remove class based on whether this is the current week
        if (isCurrentWeek && !disqualified) {
          row.classList.add('current-week');
        } else {
          row.classList.remove('current-week');
        }
      });
    });
  };

  // Handle favorite users button click
  const handleFavoriteUsersClick = () => {
    setUserSelectionType('favorite');
    firstRenderRef.current = true;
    fetchDefaultParticipantsHistory();
    //initializeUserSelection('favorite');
    const favoriteList = lists.find(list => list.type === 'favorite' && list.creator_id === userId && list.id === favoriteListValue?.value);
    const favoriteUserIds = favoriteList ? favoriteList.participants : [];
    setSelectedUsers(favoriteUserIds.map(id => {
      const user = users.find(user => user.id === id);
      return { user: user || null, required: false };
    }));
    setFavoriteListValue({ value: favoriteListValue?.value || 0, label: favoriteListValue?.label || 'お気に入りリスト選択してください' });
  };

  // Handle default participants button click
  const handleDefaultParticipantsClick = () => {
    setUserSelectionType('default');
    firstRenderRef.current = true;
    fetchDefaultParticipantsHistory();
    //initializeUserSelection('default');
    const firstDefaultList = lists.find(list => list.type === 'default' && list.creator_id === userId && list.id === defaultListValue?.value);
    const defaultUserIds = firstDefaultList ? firstDefaultList.participants : [];
    setSelectedUsers(defaultUserIds.map(id => {
      const user = users.find(user => user.id === id);
      return { user: user || null, required: false };
    }));
    setDefaultListValue({ value: defaultListValue?.value || 0, label: defaultListValue?.label || 'デフォルトリスト選択してください'});
  };

  // Initialize user selection modal based on type
  const initializeUserSelection = (type: 'favorite' | 'default') => {
    // Get all users from preload data
    const allUsers = preloadData.users || [];
    
    // Get the saved user IDs based on type
    const savedUserIds = type === 'favorite' ? favoriteUserIds : defaultParticipantUserIds;
    
    // Find the user objects for the saved IDs
    const savedUsers:ReservationsParticipant[] = allUsers.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
    
    // Set available users (those not in the saved list)
    const otherUsers:ReservationsParticipant[] = allUsers.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
    
    // Set the state variables
    setSelectedUsers(savedUsers);
    setAvailableUsers(otherUsers);
  };

  // Save selected users and close modal
  const handleSaveUserSelection = async () => {
    const selectedUserIds = selectedUsers.map(user => user.user?.id || 0) || [];

    if (userSelectionType === 'favorite') {
      if (!favoriteListValue) {
        // You must create and select a favorite list first
        alert("「お気に入りリスト」を作成して選択してください。");
        return;
      }
      await dispatch(updateList({
        id: favoriteListValue?.value || 0,
        type: 'favorite',
        participants: selectedUserIds,
        name: favoriteListValue?.label || 'お気に入りリスト'
      }));
      setFavoriteUserIds(selectedUserIds);
      setFavoriteListValue({ value: favoriteListValue?.value || 0, label: favoriteListValue?.label || 'お気に入りリスト'});
    } else if (userSelectionType === 'default') {
      if (!defaultListValue) {
        // You must create and select a default list first
        alert("「デフォルトリスト」を作成して選択してください。");
        return;
      }
      await dispatch(updateList({
        id: defaultListValue?.value || 0,
        type: 'default',
        participants: selectedUserIds,
        name: defaultListValue?.label || 'デフォルトリスト',
      }));
      setDefaultParticipantUserIds(selectedUserIds);
      setDefaultListValue({ value: defaultListValue?.value || 0, label: defaultListValue?.label || 'デフォルトリスト'});
    }
    
    // Close the modal
    setUserSelectionType(null);
  };

  // Close modal without saving
  // No users are selected, and choose the top from the list of default lists or favorite lists
  const handleCloseUserSelection = () => {
    setUserSelectionType(null);
    firstRenderRef.current = true;
    if (userSelectionType === 'favorite') {
      setFavoriteListValue({ value: favoriteListValue?.value || 0, label: favoriteListValue?.label || 'お気に入りリスト'});
    } else if (userSelectionType === 'default') {
      setDefaultListValue({ value: defaultListValue?.value || 0, label: defaultListValue?.label || 'デフォルトリスト'});
    }
  };

  // Handle month change to load new reservations
  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

 const goToBack = () => {
  let newDate = new Date(currentDate);
  if (calendarSpan === 'month') {
       // for month always set to the first day of the target month
    // to avoid issues with different month lengths
    const targetMonth = newDate.getMonth() - 1;
    const targetYear = newDate.getFullYear();
    if (targetMonth < 0) {
      newDate = new Date(targetYear - 1, 11, 1); // december of previous year
    } else {
      newDate = new Date(targetYear, targetMonth, 1); // Ffirst day of target month
    }
  }

 else if (calendarSpan === 'week') { 
    newDate.setDate(newDate.getDate() - 7); 
    calculateCurrentWeekRow(newDate);
  }
  else if (calendarSpan === 'day') { 
    newDate.setDate(newDate.getDate() - 1); 
  }
  
  setCurrentDate(newDate);
};

const goToNext = () => {
  let newDate = new Date(currentDate);
  
  if (calendarSpan === 'month') { 

    const targetMonth = newDate.getMonth() + 1;
    const targetYear = newDate.getFullYear();
    
    // Handle year transition
    if (targetMonth > 11) {
      newDate = new Date(targetYear + 1, 0, 1); // janof next year
    } else {
      newDate = new Date(targetYear, targetMonth, 1); // first day of target month
    }
  }
  else if (calendarSpan === 'week') { 
    newDate.setDate(newDate.getDate() + 7); 
    calculateCurrentWeekRow(newDate);
  }
  else if (calendarSpan === 'day') { 
    newDate.setDate(newDate.getDate() + 1); 
  }
  
  setCurrentDate(newDate);
};

  const goToCurrent = () => {
    const now = new Date();
    setCurrentDate(now);
  };

  // logic to display the date based on daily, weekly and monthly views
  let currentDateText = "";

if (calendarSpan === "month") {
  currentDateText = moment(currentDate).format("YYYY年 M月");
} else if (calendarSpan === "week") {
  const weekStart = moment(currentDate).startOf('week');  // sunday
  const weekEnd = moment(currentDate).endOf('week');   // saturday


  if (weekStart.year() === weekEnd.year() && weekStart.month() === weekEnd.month()) {
    currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("D日")}`;
  }

  else if (weekStart.year() === weekEnd.year()) {
    currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("M月 D日")}`;
  }

  else {
    currentDateText = `${weekStart.format("YYYY/M/D")}〜${weekEnd.format("YYYY/M/D")}`;
  }
} else {
  currentDateText = moment(currentDate).format("YYYY年 M月 D日");
}

  const reservationCalendarInputs = calendarUserIds.map((friendId) => {
    const friendEvents = (calendarGrouping === "individual") ? events : events.filter((event) => {
      const reservation = event.reservation;
      return reservation?.participants.some((participant) => participant.user.id === friendId) || reservation?.creator_id === friendId;
    });
    const friendUser = preloadData.users.find((user) => user.id === friendId);
    const friendName = friendUser ? `${friendUser.last_name} ${friendUser.first_name}` : "Unknown User";
    const isUserCalendar = (friendId === userId);
    const displayName = calendarGrouping === "individual" ? false : calendarSpan === "week" ? true : isUserCalendar ? true : true;

    return {
      key: friendId, // user id, e.g. 7
      name: friendName,
      events: friendEvents,
      isUserCalendar: isUserCalendar,
      displayName: displayName,
    };
  });

  const flatUserResources = reservationCalendarInputs.map(input => ({
    id: input.key,
    title: input.name,
  }));


  const flatUserInputs = reservationCalendarInputs
      .filter(input => input.events && Array.isArray(input.events))
      .reduce((acc, input) => {
        const key = input.key || 0;
        
        acc.push(...input.events.map(event => ({
          ...event,
          userKey: key,
          userName: input.name,
          resourceId: key,
          start: event.startDate,
          end: event.endDate,
          creatorId: event.reservation?.creator_id,
        })));
      
      return acc;
    }, [] as Array<any>);

  const facilityCalendarInputs = calendarRoomIds.map((roomId) => {
    const roomEvents = events.filter((event) => {
      const reservation = event.reservation;
      return reservation?.room_ids.some((room) => room === roomId);
    });
    const room = rooms.data.find((room) => room.id === roomId);
    const roomName = room ? room.name : "Unknown Room";

    return {
      key: roomId,
      name: roomName,
      events: roomEvents,
      isUserCalendar: false, // data broken into rooms, not by user, so it's never the user's calendar
      displayName: true,     // always show the name of the room, there's no view we don't want to show it
    };
  });

  const flatFacilityResources = facilityCalendarInputs.map(input => ({
    id: input.key,
    title: input.name,
  }));

  const flatFacilityInputs = facilityCalendarInputs
    .filter(input => input.events && Array.isArray(input.events))
    .reduce((acc, input) => {
      const key = input.key || 0;
      
      acc.push(...input.events.map(event => ({
        ...event,
        facilityKey: key,
        facilityName: input.name,
        resourceId: key,
        start: event.startDate,
        end: event.endDate,
      })));
    
    return acc;
  }, [] as Array<any>);

  const dailyFacilityGrouping = (calendarSpan === "day" && calendarGrouping === "facilities");
  const dailyUserGouping = (calendarSpan === "day" && calendarGrouping !== "individual" && calendarGrouping !== "facilities");
  const dailyGrouping = dailyFacilityGrouping || dailyUserGouping;

  // const calendarInputs =
  //   dailyFacilityGrouping ? [flatFacilityInputs] :
  //   facilitiesDisplayed ? facilityCalendarInputs :
  //   dailyUserGouping ? [flatUserInputs] :
  //   reservationCalendarInputs;

  const calendarInputs = useMemo(() => {
  if (facilitiesDisplayed && facilityCalendarInputs.length === 0) return [];
  if (dailyFacilityGrouping && flatFacilityInputs.length === 0) return [];
  if (dailyUserGouping && flatUserInputs.length === 0) return [];
  if (!facilitiesDisplayed && reservationCalendarInputs.length === 0) return [];

  return dailyFacilityGrouping ? [flatFacilityInputs] :
         facilitiesDisplayed ? facilityCalendarInputs :
         dailyUserGouping ? [flatUserInputs] :
         reservationCalendarInputs;
}, [
  facilitiesDisplayed,
  dailyFacilityGrouping,
  dailyUserGouping,
  facilityCalendarInputs,
  flatFacilityInputs,
  flatUserInputs,
  reservationCalendarInputs
]);

  // 施設予約カレンダーの初期化
  useEffect(() => {
    if (firstRender){
    if (calendarSpan === 'week' && calendarGrouping === 'facilities') {
        const monthViews = document.querySelectorAll('.rbc-month-view');
        monthViews.forEach(monthView => {
          monthView?.classList.add('rbc-month-view-short');
            calculateCurrentWeekRow(currentDate);
            setFirstRender(false);
        });
      }
    }
  }, [calendarInputs]);

  const calenderResources = dailyFacilityGrouping ? flatFacilityResources :
    dailyUserGouping ? flatUserResources :
    undefined;

  const isReady = loaded &&
  (!facilitiesDisplayed || (calendarInputs.length > 0 && calendarRoomIds.length > 0)) &&
  (!facilitiesDisplayed || scheduleRoomList?.length > 0);

  const todayLabel = calendarSpan === "day"
  ? "今日"
  : calendarSpan === "week"
    ? "今週"
    : calendarSpan === "month"
      ? "今月"
      : "今日";

  const dateControlBar = (
    <div style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      marginTop:"0.5rem",

    }}>
      <span style={{
        fontSize: isMobile ? "16px" : "20px", 
        display: "flex", 
        alignItems: "center", 
        fontWeight: "bold",
        }}>
        {currentDateText}
      </span>
      <div style={{ display: "flex", alignItems: "center" }}>
        {!isReady
        // (facilitiesDisplayed && (calendarInputs.length === 0 ||calendarRoomIds.length === 0 || sortedGroups.length === 0)) 
        ?
           <div>読み込み中...</div>
         : null}
        <div className="btn-back">
          <AppIconButton onClick={goToBack} icon={<ArrowLeftIcon />} />
        </div>
          <label
            className="btn-current"
            onClick={goToCurrent}
            style={{
              cursor: "pointer",
              fontSize: "11px",
              fontWeight: "bold",
              margin:"0 1rem",
              backgroundColor: "#666",
              borderRadius: "6px",
              color: "white",
              padding: "3px 4px"
              }}>
              {todayLabel}
          </label>
        <div className="btn-next">
          <AppIconButton onClick={goToNext} icon={<ArrowRightIcon />} />
        </div>
      </div>
      {/*{reservationsLoading && <span style={{ display: "flex", alignItems: "center", fontSize: "2rem", color: "#666" }}>(読み込み中...)</span>}*/}
    </div>
  );

  // console.log("calendarInputs", calendarInputs);
  // event内のreservation, is_private trueで非公開

  // 予約タイプ選択やキーワード検索時のフィルター
  const filteredEventsList = useMemo(() => {
    if (!calendarInputs || !Array.isArray(calendarInputs)) return [];

    return calendarInputs.map((input) => {

      if (
        calendarGrouping === "facilities" ||
        dailyGrouping ||
        !input?.events ||
        !Array.isArray(input.events)
      ) {
        return input.events ?? [];
      }

      let eventsList = input.events;

      if (privateSelected !== "all") {
        eventsList = eventsList.filter((e) => {
          const isPrivate = e.reservation?.is_private;

          if (privateSelected === "public") {
            return isPrivate === false;
          }

          if (privateSelected === "private") {
            return isPrivate === true;
          }

          return true;
        });
      }

      return eventsList.filter((e) => {
        const typeMatch = !selectedType || e.type === selectedType.value;
        const keywordMatch =
          !searchKeyword || e.title?.toLowerCase().includes(searchKeyword.toLowerCase());
        return typeMatch && keywordMatch;
      });
    });
  }, [calendarInputs, selectedType, searchKeyword, calendarGrouping, dailyGrouping, privateSelected]);

  const calenderElements = !isReady ? <div>読み込み中...</div> : calendarInputs.map((input, idx) => {
    if (calendarGrouping === "individual" && idx > 0) return null;

    const filteredEvents = filteredEventsList[idx];

    return(
      <ScheduleCalendar
        key={`facility-${input.key}-${calendarSpan}-${calendarGrouping}-${calendarRoomIds.join('-')}`}
        calenderUserId={(calendarGrouping === "individual" ? selectedUserId : input.key) || 0}
        facilityId={facilitiesDisplayed ? input.key : undefined}
        instanceKey={`${input.key}-${calendarSpan}-${calendarGrouping}`}
        calendarIndex={idx + 1}
        currentDate={currentDate}
        resources={calenderResources}
        events={dailyGrouping ? input : filteredEvents}
        span={calendarSpan}
        grouping={calendarGrouping}
        holidays={holidays ?? []}
        defaultParticipants={defaultParticipantUserIds}
        ownCalendar={dailyGrouping ? true : userId === input.key}
        name={input.name}
        onNavigateRequest={handleNavigate}
        dailyGouping={dailyGrouping}
        refreshReservations={refreshReservations}
        defaultParticipantsHistory={defaultParticipantsHistory}
        storeDefaultParticipantsSelection={storeDefaultParticipantsSelection}
        openEventRequest={openEventRequest}
      />
    );
  });

  const allCalendarElements = (

    <div id="allCalendarElements" style={{
      display: "flex",
      flexDirection: stackedView ? "column" : columnsView ? "row" : undefined,
      width: "100%",
      flex: 1,
      minHeight: 0,
      overflow:"auto", // IMPORTANT: this setting enabled scrollbars around the calendar
      border:"2px solid white",
      borderRadius:"10px",
      marginTop:"0.5rem",
      overscrollBehavior: "none",
      position:"relative"
      }}>
  
        <div id="headerDummy"
          ref={headerDummyRef}
        style={{
            position:"fixed",
            zIndex:"30",
            flexShrink: 0,
            width: isMobile ? '100px' : '150px',
            minWidth: isMobile ? '100px' : '150px',
            height: '25px',
            display: "none",
            background:"#e4e4e4",
            borderRight:"1px solid #bbb"
        }}/>

        <div id="headerDummyIndividualWeekly"
          ref={headerDummyIndividualWeeklyRef}
        style={{
            position:"fixed",
            zIndex:"30",
            flexShrink: 0,
            width: "46x",
            minWidth: "46px",
            height: '0px',
            display: "none",
            background:"#e4e4e4",
            borderRight:"1px solid #bbb"
        }}/>
      
      {calenderElements}
  
    </div>);

  const buttonsContent = [
    {text: "デフォルト予定参加者登録", function: handleDefaultParticipantsClick, hiddenOnFacilities: true},
    {text: "お気に入りメンバー登録", function: handleFavoriteUsersClick, hiddenOnFacilities: true},
  ];

  const headerButtons = buttonsContent.map((button, index) => (
    <div onClick={button.function}
    >
      <Button
        key={index}
        variant="contained"        
        sx={{
          fontSize: '11px',
          fontWeight: 'bold',
          paddingTop: '1rem',
          bgcolor: '#ff6a0c',
          '&:hover': { bgcolor: '#e05a00', boxShadow: 'none' },
          height: isMobile ? "30px" : "40px",
          borderRadius: '50px',
          display: (button.hiddenOnFacilities && facilitiesDisplayed) ? "none" : undefined,
          boxShadow: 'none',
        }}
      >
        {button.text}
      </Button>
    </div>
  ));

  // クエリ反映のための共通pushFilters関数
  const pushFilters = (overrides: Partial<Record<string, string | number | undefined>> = {}) => {
    // 画面に応じてベースクエリを分岐
    const base: Record<string, any> = facilitiesDisplayed
      ? {
          span: calendarSpan,
          roomId: selectedRoomId?.value ?? undefined,
        }
      : {
          view: calendarGrouping,
          typeId: selectedType?.value ?? undefined,
          q: searchKeyword ?? undefined,
          visibility: privateSelected,
          span: calendarSpan,
        };

    if (!facilitiesDisplayed) {
      if (base.view === "individual") base.userId = selectedUserId ?? undefined;
      if (base.view === "group") base.groupId = selectedGroupId ?? undefined;
      if (base.view === "favorites") base.listId = favoriteListValue?.value ?? undefined;
    }

    const merged = { ...base, ...overrides };
    navigateWithState(currentPath, merged, "push");
  };

  // URL -> state 復元（戻る/進む・直リンク対応 - 予約カレンダー用）
  useEffect(() => {
    if (facilitiesDisplayed) return; // 施設予約は別の復元ロジックで処理
    const viewParam = getQueryParam("view");
    const userIdParam = getQueryParam("userId");
    const groupIdParam = getQueryParam("groupId");
    const listIdParam = getQueryParam("listId");
    const typeIdParam = getQueryParam("typeId");
    const qParam = getQueryParam("q");
    const visibilityParam = getQueryParam("visibility");
    const spanParam = getQueryParam("span");

    // クエリ欠如時の初期状態復元
    if (!viewParam && calendarGrouping !== "individual") {
      setCalendarGrouping("individual");
    }

    if (viewParam === "individual" || viewParam === "group" || viewParam === "favorites") {
      if (calendarGrouping !== viewParam) setCalendarGrouping(viewParam as typeof calendarGrouping);
    }

    // 個人ビュー: userId が無い場合はログイン者に戻す
    if ((viewParam === "individual" || !viewParam) && !userIdParam && userId) {
      if (selectedUserId !== userId) setSelectedUserId(userId);
    } else if (viewParam === "individual" && userIdParam) {
      const userId = Number(userIdParam);
      if (selectedUserId !== userId) setSelectedUserId(userId);
    }

    // グループビュー: groupId が無い場合はログイン者が所属するグループに戻す（先頭を選択）
    if (viewParam === "group" && !groupIdParam) {
      const myGroupIds: number[] = Array.from(new Set(
        (groupsUsers || [])
          .filter((gu: any) => Number(gu.user_id) === Number(userId))
          .map((gu: any) => Number(gu.group_id))
      ));
      if (myGroupIds.length > 0) {
        const fallbackGroupId: number | null = typeof myGroupIds[0] === "number" ? myGroupIds[0] : null;
        if (fallbackGroupId !== null && selectedGroupId !== fallbackGroupId) {
          setSelectedGroupId(fallbackGroupId as number);
        }
      }
    } else if (viewParam === "group" && groupIdParam) {
      const groupId = Number(groupIdParam);
      if (selectedGroupId !== groupId) setSelectedGroupId(groupId);
    }
    if (viewParam === "favorites" && listIdParam) {
      const lidNum = Number(listIdParam);
      const fav = lists.find(list => list.type === 'favorite' && Number(list.id) === lidNum);
      const next = fav ? { value: fav.id, label: fav.name || 'お気に入りリスト' } : { value: lidNum, label: '' };
      if (!favoriteListValue || Number(favoriteListValue.value) !== Number(next.value)) {
        setFavoriteListValue(next as any);
      }
    } else if (viewParam === "favorites" && !listIdParam) {
      // listId が消えた場合は一番上のリストに戻す
      const firstFavorite = lists.find(list => list.type === 'favorite');
      if (firstFavorite) {
        const next = { value: firstFavorite.id, label: firstFavorite.name || 'お気に入りリスト' };
        if (!favoriteListValue || Number(favoriteListValue.value) !== Number(next.value)) {
          setFavoriteListValue(next as any);
        }
      }
    }

    if (typeof typeIdParam === "string") {
      const tIdNum = Number(typeIdParam);
      if (!isNaN(tIdNum)) {
        const matchedType = types?.data?.find((typeItem: any) => Number(typeItem.id) === tIdNum);
        const nextType = matchedType ? { value: matchedType.id, label: matchedType.text } : { value: tIdNum, label: "" };
        if (!selectedType || Number(selectedType.value) !== Number(nextType.value)) {
          setSelectedType(nextType as any);
        }
      } else if (selectedType) {
        setSelectedType(null);
      }
    } else if (selectedType) {
      setSelectedType(null);
    }

    if ((qParam ?? "") !== searchKeyword) {
      setKeywordInput(qParam ?? "");
      setSearchKeyword(qParam ?? "");
    }

    // visibility が無い場合は "public" に戻す
    if (!visibilityParam) {
      if (privateSelected !== "public") setprivateSelected("public");
    } else if (visibilityParam === "all" || visibilityParam === "public" || visibilityParam === "private") {
      if (privateSelected !== visibilityParam) {
        setprivateSelected(visibilityParam as typeof privateSelected);
      }
    }

    // span の復元（クエリが存在する場合のみ適用。無い場合は view に応じて既定化）
    if (spanParam === "month" || spanParam === "week" || spanParam === "day") {
      // 個人 view からグループやお気に入り view に遷移した場合の処理
      // グループ/お気に入りで month は無効 → week を優先（URL値よりUI整合を優先）
      const normalizedSpan =
        (viewParam === "group" || viewParam === "favorites") && spanParam === "month"
          ? "week"
          : spanParam;
      if (calendarSpan !== normalizedSpan) setCalendarSDpan(normalizedSpan as typeof calendarSpan);
    } else {
      // group/favorites は週、individual は月
      const fallbackSpan = (calendarGrouping === "group" || calendarGrouping === "favorites") ? "week" : "month";
      if (calendarSpan !== fallbackSpan) setCalendarSDpan(fallbackSpan as typeof calendarSpan);
    }
  }, [currentQueryString, types?.data, lists, facilitiesDisplayed, calendarGrouping, selectedUserId, selectedGroupId, favoriteListValue, selectedType, searchKeyword, privateSelected, calendarSpan, getQueryParam]);

  // URL -> state 復元（戻る/進む・直リンク対応 - 施設予約用）
  useEffect(() => {
    if (!facilitiesDisplayed) return;
    const spanParam = getQueryParam("span");
    const roomIdParam = getQueryParam("roomId");

    // span: 施設予約は week/day。欠如時は week。
    if (spanParam === "week" || spanParam === "day") {
      if (calendarSpan !== spanParam) setCalendarSDpan(spanParam as typeof calendarSpan);
    } else {
      if (calendarSpan !== "week") setCalendarSDpan("week");
    }

    // roomId: 欠如時は未選択（全室表示）
    if (roomIdParam) {
      const rid = Number(roomIdParam);
      const room = (scheduleRoomList || []).find((r: any) => Number(r.id) === rid);
      const next = room ? { value: room.id, label: room.name } : null;
      if (next) {
        if (!selectedRoomId || Number(selectedRoomId.value) !== Number(next.value)) {
          setSelectedRoomId(next as any);
        }
      } else {
        if (selectedRoomId) setSelectedRoomId(null);
      }
    } else {
      if (selectedRoomId) setSelectedRoomId(null);
    }
  }, [currentQueryString, facilitiesDisplayed, scheduleRoomList, selectedRoomId, calendarSpan, getQueryParam]);

  const groupingButtons = reservationsDisplayed ? (
    <CalendarLengthButtonGroup>
      <CalendarLengthButton
        className="calendar-length-button grouping-individual"
        selected={calendarGrouping === "individual"}
        onClick={() => { setCalendarGrouping("individual"); pushFilters({ view: "individual" }); }}
      >
        個人
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-group"
        selected={calendarGrouping === "group"}
        onClick={() => { setCalendarGrouping("group"); pushFilters({ view: "group" }); }}
      >
        グループ
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-favorites"
        selected={calendarGrouping === "favorites"}
        onClick={() => { setCalendarGrouping("favorites"); pushFilters({ view: "favorites" }); }}
      >
        お気に入りメンバー一覧
      </CalendarLengthButton>
    </CalendarLengthButtonGroup>
  ) : null;

  const privateButtons = reservationsDisplayed ? (
    <CalendarLengthButtonGroup>
      <CalendarLengthButton
        className="calendar-length-button grouping-individual"
        selected={privateSelected === "all"}
        onClick={() => { setprivateSelected("all"); pushFilters({ visibility: "all" }); }}
      >
        全て
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-group"
        selected={privateSelected === "public"}
        onClick={() => { setprivateSelected("public"); pushFilters({ visibility: "public" }); }}
      >
        公開のみ
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-favorites"
        selected={privateSelected === "private"}
        onClick={() => { setprivateSelected("private"); pushFilters({ visibility: "private" }); }}
      >
        非公開のみ
      </CalendarLengthButton>
    </CalendarLengthButtonGroup>
  ) : null;

  //ユーザーとグループのリアルタイム履歴反映のリスト読み込み
    useEffect(() => {
      if (!list || !groupList || !userId || list.length === 0) return;

      if (list && groupList) {
        
        const userOption = list.map((user) => ({
          label: `${user.last_name} ${user.first_name}`,
          value: user.id.toString(),
          type: user.type,
        }));
        
        const currentUserOption = userOption.find((user) => user.value === userId?.toString());
  
        const userOptionWithoutCurrentUser = userOption.filter((user) => user.value !== userId?.toString());
        
        const groupOption = groupList.map((group) => ({
          label: group.name,
          value: group.id.toString(),
          type: group.type,
        }));
  
        setUserOptions(
          [currentUserOption, ...userOptionWithoutCurrentUser].filter(
            (option): option is { label: string; value: string; type: string } => option !== undefined
          )
        );

        setGroupOptions(groupOption);

        // 初回アクセス時にグループが選択されていない場合、最初のグループを選択
        if (selectedGroupId === null && groupOption.length > 0) {
          const firstGroup = groupOption[0];
          if (firstGroup && firstGroup.value) {
            setSelectedGroupId(Number(firstGroup.value));
          }
        }
  
      }
    }, [list, groupList]);





  const handleOnChange = (value: string) => {
    setKeywordInput(value.trim() ?? "");
    //if (value.trim() === "") { setSearchKeyword(""); }
  };

  useEffect(() => {
    if (userSelectionType === null) return;
    fetchDefaultParticipantsHistory();
  }, [userSelectionType, fetchDefaultParticipantsHistory]);

  // Map history to list IDs. Supports either shape:
  //  { id } or { selected_schedule_list_id }
  const historyIds = Array.from(new Set((defaultParticipantsHistory ?? [])
    .map(h => (typeof h?.selected_schedule_list_id !== "undefined" ? h.selected_schedule_list_id : h?.id))
    .filter((v): v is number | string => v !== undefined && v !== null)));

  // Need to use the index as the rank
  const rankMap = new Map<number | string, number>();
  historyIds.forEach((id, idx) => rankMap.set(id, idx));

  const listsDropdownOptions = lists
    .filter(list => (list.type === userSelectionType))
    .filter(list => (list.creator_id === userId)) // only lists created by the current user - should not be needed, but just in case
    .map((list, origIdx) => ({ // add the index (rank), lower is higher on the list
      list, origIdx, rank: rankMap.has(list.id) ? (rankMap.get(list.id) as number) : Number.POSITIVE_INFINITY
    }))
    .sort((a, b) => { // sort by rank
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.origIdx - b.origIdx;
    })
    .map(({ list }) => ({ // map to the select options
      value: list.id,
      label: list.name || (userSelectionType === "favorite" ? "お気に入りリスト" : "デフォルトリスト"),
    }));

  listsDropdownOptions.unshift({ value: 0, label: "リストを選択してください" });

  const hideEditOptions = (listsDropdownOptions.length <= 1);

  // console.log("All lists again:", lists, ", userSelectionType", userSelectionType, ", listsDropdownOptions", listsDropdownOptions);

//  useEffect(() => {
//    firstRenderRef.current = false;
//    // console.log("listsDropdownOptions", listsDropdownOptions);
//    if (!listsDropdownOptions.length) return;
//
//    const top = listsDropdownOptions[0];
//    const current = userSelectionType === "favorite" ? favoriteListValue : defaultListValue;
//
//    // Only update if different to avoid loops
//    if (!current || current.value !== top.value) {
//      if (userSelectionType === "favorite") {
//        setFavoriteListValue(top);
//      } else {
//        setDefaultListValue(top);
//      }
//    }
//  }, [ listsDropdownOptions, userSelectionType, favoriteListValue, defaultListValue ]);

  //施設予約のドロップダウン
  const facilitySelectionDropdown = facilitiesDisplayed ? (
    <Grid2 container size={{xs:12, md:12, lg:4, xl:3}} style={{marginTop:"1rem"}}>
      <Grid2 size={{xs:12}}>
        <AppCustomeSelect
          value={selectedRoomId}
          options={roomOptions ?? []}
          style={{width: "24rem"}}
          onChange={(option) => {
            // console.log("Selected option:", option);
            setSelectedRoomId(option ? option : null);

            // 施設予約のカレンダーを更新
            // 週カレンダーのuseEffectを明示的に呼び出して、カレンダーを更新する
            if (calendarSpan === "week"){
              setCalendarSDpan("day");
              setTimeout(() => setCalendarSDpan("week"), 10);
            }

            // 選択した施設の履歴保存
            if (option) {
            storeScheduleRoomSelection({ user_id: userId, selected_schedule_room_id: Number(option.value) });
            }

            // URLクエリへ反映（roomId）
            pushFilters({ roomId: option ? Number((option as any).value) : undefined });
          }}
          placeholder="施設を選択してください"
          isSearchable={true}
          isClearable={true}
          className="user-selection-dropdown"
        />
      </Grid2>
    </Grid2>
  ) : null;

  const showMonthButton = (calendarGrouping === "individual" && reservationsDisplayed) ? true : false; 

  const hideSchedule = false;

  const paddingBottom = useDynamicPadding();
  

  if (!userId || !preloadData.users || preloadData.users.length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f5f5"
      }}>
        <Box position="relative" display="inline-flex">
          {/* bottom one is the full circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={60}
            thickness={6}
            sx={{ color: "#ff6a0c" }}
          />
          {/* rotating arc */}
          <CircularProgress
            variant="indeterminate"
            size={60}
            thickness={6}
            sx={{
              color: "#0873fc",
              position: "absolute",
              left: 0,
              top: 0,
            }}
          />
        </Box>
        <span style={{ marginTop: "1rem", color: "#888", fontSize: "20px" }}>スケジュール読み込み中...</span>
      </div>
    );
  }

  return ( (hideSchedule) ? <span style={{lineHeight: "250px"}}>Under construction</span> :
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: isMobile ? "100svh" : "100vh",
        background: "#f5f5f5",
        overflow: "hidden",
        paddingTop: isMobile ? "7rem" : "8rem",
        paddingBottom: isMobile ? "0.5rem" : "1.5rem",
        paddingLeft: isMobile ? "0.5rem" : "1.5rem",
        paddingRight: isMobile ? "0.5rem" : "1.5rem",
      }}
    >
    
      <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" , width: "100%"}}>
        <ScheduleTitle>{facilitiesDisplayed ? "施設予約" : "スケジュール"}</ScheduleTitle>
        <CollapsibleFiltersWrapper collapsed={isMobile && filtersCollapsed}>
        <ScheduleHeaderContainer>
          <ScheduleHeaderButtons>
            {headerButtons}
          </ScheduleHeaderButtons>
        </ScheduleHeaderContainer>
        <StyledDivider />
        {groupingButtons}

        {/* grid for organizing dropdowns */}
        {(reservationsDisplayed && (calendarGrouping === "individual" || calendarGrouping === "group" || calendarGrouping === "favorites")) && (
          <Grid container spacing={1} style={{ marginTop: "0rem" }}>
            {/* first part: Individual/Group/Favorite Selection */}
            <Grid item xs={12} md={3} sm={12}>
              {calendarGrouping === "individual" && (
                <AppCustomeSelect
                  style={{ fontSize: isMobile ? "11px" : "13px" }}
                  value={
                    userOptions
                      ? userOptions.find((opt) => opt.value === String(selectedUserId)) || null
                      : null
                  }
                  options={userOptions ?? []}
                  onChange={(option) => {
                    setSelectedUserId(option ? Number(option.value) : null);
                    storeSelection({ user_id: userId, selected_user_id: option.value });
                    pushFilters({ userId: option ? Number(option.value) : undefined });
                  }}
                  placeholder="個人を選択してください"
                  isSearchable={true}
                  isClearable={false}
                  name="user-selector"
                  aria-label="ユーザー選択"
                  playwrightName="user-selector"
                />
              )}
              {calendarGrouping === "group" && (
                <AppCustomeSelect
                  style={{ fontSize: isMobile ? "11px" : "13px" }}
                  value={
                    groupOptions
                      .find((opt) => opt.value === String(selectedGroupId)) || null
                  }
                  options={groupOptions ?? []}
                  onChange={(option) => {
                    setSelectedGroupId(option ? Number(option.value) : null);
                    storeGroupSelection({ user_id: userId, selected_group_id: option.value });
                    pushFilters({ groupId: option ? Number(option.value) : undefined });
                  }}
                  placeholder="グループを選択してください"
                  isSearchable={false}
                  isClearable={false}
                />
              )}
              {calendarGrouping === "favorites" && (
                <AppCustomeSelect
                  value={favoriteListValue}
                  options={lists
                    .filter((list) => list.type === 'favorite')
                    .map((list) => ({
                    value: list.id,
                    label: list.name,
                  }))}
                  onChange={(option) => {
                    console.log("Selected option:", option);
                    setFavoriteListValue(option ? option : null);
                    pushFilters({ listId: option ? (option as any).value : undefined });
                  }}
                  placeholder="リストを選択してください"
                  isSearchable={false}
                  isClearable={false}
                  className="user-selection-dropdown"
                />
              )}
            </Grid>

            {/* second part: reservation Type Selection */}
            <Grid item xs={12} md={3} sm={12}>
              <AppCustomeSelect
                style={{
                  fontSize: isMobile ? "11px" : "13px",
                  textAlign: 'left'
                }}
                value={selectedType}
                options={types.data ?
                  types.data.map((t) => ({
                    value: t.id,
                    label: t.text
                  }))
                  : []}
                onChange={(option) => {
                  setSelectedType(option ? option : null);
                  pushFilters({ typeId: option ? (option as any).value : undefined });
                }}
                placeholder="予定タイプを選択"
                isSearchable={false}
                isClearable={true}
              />
            </Grid>

            {/* third part: Keyword Search */}
            <Grid item xs={12} md={3} sm={12}>
              <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <ScheduleKeywordSearchWrapper>
                    <AppInput
                      value={keywordInput}
                      iconStart={<SearchIcon />}
                      handleChange={handleOnChange}
                      placeholder="予定タイトル検索"
                      disabled={false}
                      name="searchValue"
                      isSearch={true}
                    />
                  </ScheduleKeywordSearchWrapper>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <SearchButton selected={true} onClick={() => { setSearchKeyword(keywordInput); pushFilters({ q: keywordInput ?? undefined }); }}>
                    検索
                  </SearchButton>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
        {!facilitiesDisplayed ? <StyledDivider /> : null}
        {privateButtons}

        {facilitySelectionDropdown}
        </CollapsibleFiltersWrapper>
        {isMobile ? <div style={{ height: "1rem" }} /> : <StyledDivider />}
        <CalendarLengthButtonGroup>
        <CalendarLengthButton
          className="calendar-length-button"
          style={{ display: showMonthButton ? undefined : "none" }}
          selected={calendarSpan === "month"}
          onClick={() => { setCalendarSDpan("month"); pushFilters({ span: "month" }); }}
          data-playwright="schedule-month-button"
        >
          月
        </CalendarLengthButton>
        <CalendarLengthButton
          className="calendar-length-button"
          selected={calendarSpan === "week"}
          onClick={() => { setCalendarSDpan("week"); pushFilters({ span: "week" }); }}
          data-playwright="schedule-week-button"
        >
          週
        </CalendarLengthButton>
        <CalendarLengthButton
          className="calendar-length-button"
          selected={calendarSpan === "day"}
          onClick={() => { setCalendarSDpan("day"); pushFilters({ span: "day" }); }}
          data-playwright="schedule-day-button"
        >
          日
        </CalendarLengthButton>
        </CalendarLengthButtonGroup>

        {isMobile && (
          <FilterToggleBar onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
            {filtersCollapsed ? "▼" : "▲"}
          </FilterToggleBar>
        )}

        {/*<span style={{display: "none"}}>Grouping: {calendarGrouping}, Length: {calendarSpan}, Stacked: {stackedView ? "true" : "false"}, Columns: {columnsView ? "true" : "false"}, isMobile: {isMobile ? "true" : "false"}</span>*/}
        {dateControlBar}
      </div>
      
      {/*<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>*/}
      {allCalendarElements}
      {/* User Selection Modal */}
      <Dialog
        open={userSelectionType !== null}
        onClose={handleCloseUserSelection}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
        sx: {
          ...(!isMobile && { borderRadius: '10px', width: "600px" }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'hidden',
            '& > .MuiBox-root': {
              padding: '0',
              paddingTop:"3rem"
              },
          }}
        >
          <Box sx={{
        flexGrow: 1, // This Box will take up available space before the action buttons
        overflowY: 'auto', // Make this specific Box scrollable if its content overflows
        p: { xs: 2, sm: 3 }, // Consistent padding around the main content
      }}>
        
            <FormTitleHeader>
              {userSelectionType === 'favorite' ? 'お気に入りメンバー登録' : 'デフォルト予定参加者登録'}
              <IconButton
                  aria-label="close"
                  onClick={handleCloseUserSelection}
                  sx={{ color: (theme) => theme.palette.grey[700],
                  fontSize:'24px',
                  padding: 0,
                  paddingLeft:'4px',
                  paddingRight:'4px',
                  '&:hover': {
                  backgroundColor: 'transparent',
              }}}>
                  ×
              </IconButton>
            </FormTitleHeader>
            <div style={{display: userSelectionType === 'favorite' ? "flex" : "flex", flexDirection:"column", gap:"1rem", padding:"1rem", marginTop:"1rem"}}>
              <AppCustomeSelect
                style={{
                  fontSize:"13px",
                  display: hideEditOptions ? "none" : undefined,
                }}
                value={userSelectionType === 'favorite' ? favoriteListValue : defaultListValue}
                options={ listsDropdownOptions }
                onChange={(option) => {
                  // console.log("Selected option:", option);
                  userSelectionType === 'favorite' ? setFavoriteListValue(option ? option : null) : setDefaultListValue(option ? option : null);
                  setSelectedUsers(
                    option ? lists.find(list => list.id === option.value)?.participants.map(userId => ({
                      user: users.find(user => user.id === userId) || null,
                      required: false
                    })) || [] : []
                  );
                  if (option?.value) {
                    storeDefaultParticipantsSelection({ user_id: userId, selected_schedule_list_id: option.value });
                  }
                }}
                placeholder="リストを選択してください"
                isSearchable={false}
                isClearable={false}
                className="user-selection-dropdown"
              />
              <Box sx={{
                display: 'flex',
                gap: '5px',
                padding: '0',
                width:"100%",
                '& > *': { // Target direct children (PassChangeButton components)
                  flex: 1, // Make buttons share the available width
                  padding:0
                },
                }}>
              
                <PassChangeButton
                  onClick={async () => {
                    // This should never happen, it means the user select modal is open, but we don't know what type of list we're making, default or favorite
                    if (userSelectionType === null) {
                      alert("リストの種類を選択してください");
                      return;
                    }
                    // can't make more than 10 lists of one type
                    const numberOfLists = userSelectionType === 'favorite' ? lists.filter(list => list.type === "favorite").length : lists.filter(list => list.type === "default").length;
                    if (numberOfLists >= 10) {
                      alert("リストは10個まで作成できます。");
                      return;
                    }

                    const newName = prompt("新しいリストの名前を入力してください:");
                    if (newName) {
                      try {
                        const resultAction = await dispatch(createList({
                          type: userSelectionType,
                          participants: [],
                          name: newName
                        }));
                        // console.log("List created:", resultAction);
                        userSelectionType === 'favorite' ? setFavoriteListValue({ value: resultAction.payload.data.id, label: newName }) : setDefaultListValue({ value: resultAction.payload.data.id, label: newName });
                        setSelectedUsers([]);
                        setAvailableUsers(users.map(user => ({ user, required: false })));
                      } catch (error) {
                        console.error("Error creating list:", error);
                        alert("リストの作成に失敗しました。");
                      }
                    }
                  }}
                  style={{maxWidth: "33%"}}
                >
                  リストを作成する
                </PassChangeButton>
    

          
                <PassChangeButton
                  onClick={async () => {
                    if (userSelectionType === null) {
                      alert("リストの種類を選択してください");
                      return;
                    }
                    const listValue = userSelectionType === 'favorite' ? favoriteListValue : defaultListValue;
                    if (!listValue || !listValue?.value) {
                      alert("リストを選択してください");
                      return;
                    }
                    const newName = prompt("新しいリストの名前を入力してください:", listValue.label);
                    if (newName) {
                      await dispatch(updateList({
                        id: listValue.value,
                        type: userSelectionType,
                        name: newName
                      }));

                      userSelectionType === 'favorite' ? setFavoriteListValue({ value: listValue.value, label: newName }) : setDefaultListValue({ value: listValue.value, label: newName });
                    }
                  }
                  }
                  disabled={!(userSelectionType === 'favorite' ? favoriteListValue : defaultListValue)}
                  style={{display: hideEditOptions ? "none" : undefined}}
                >
                  リストの名前変更
                </PassChangeButton>
      

                <PassChangeButton
                  onClick={async () => {
                    if (userSelectionType === null) {
                      alert("リストの種類を選択してください");
                      return;
                    }
                    const valueToDelete = userSelectionType === 'favorite' ? favoriteListValue : defaultListValue;
                    if (!valueToDelete || !valueToDelete.value) {
                      alert("リストを選択してください");
                      return;
                    }

                    const confirmDelete = window.confirm("リストを削除しますか？この操作は取り消せません。");
                    if (!confirmDelete) return;

                    await dispatch(deleteList(valueToDelete.value));
                    const newSelection = listsDropdownOptions[0] || null;
                    userSelectionType === 'favorite' ? setFavoriteListValue(newSelection ? { value: newSelection.value || 0, label: newSelection.label || 'お気に入りリスト選択してください' } : null) : setDefaultListValue(newSelection ? { value: newSelection.value || 0, label: newSelection.label || 'デフォルトリスト選択してください' } : null);
                  }}
                  danger={true}
                  style={{display: hideEditOptions ? "none" : undefined}}
                >
                  リストを削除
                </PassChangeButton>
            
              </Box>
            </div>
            <div
            style={{
                padding: isMobile ? "1rem" : "2rem"
              }}
              >
            <ParticipantsSelector
              currentUserId={userId || 0}
              users={users}
              groups={sortedGroups}
              groupsUsers={groupsUsers}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              canSelectSelf={userSelectionType === 'default'}
              userSelectionType={userSelectionType}
              leftListHeight={isMobile ? "450px" : "500px"} 
              rightListHeight={isMobile ? "352px" : "402px"} 
              defaultParticipantsHistory={defaultParticipantsHistory}
              storeDefaultParticipantsSelection={storeDefaultParticipantsSelection}
            />   
            </div>
            {/* Action Buttons */}
            <Box sx={{
        display: 'flex',
        flexShrink: 0, // Prevent this Box from shrinking
        p: { xs: 1.5, sm: 2 }, // Padding for the actions area
        gap: "1rem", // Space between buttons
        borderTop: (theme) => `1px solid ${theme.palette.divider}`, // Visual separation
        backgroundColor: (theme) => theme.palette.background.paper, // Ensure background consistency
        '& > *': { // Target direct children (PassChangeButton components)
          flex: 1, // Make buttons share the available width
        },
      }}>
              <PassChangeButton
                onClick={handleCloseUserSelection}
                danger={true}
              >
                キャンセル
              </PassChangeButton>
              <PassChangeButton
                onClick={handleSaveUserSelection}
              >
                保存
              </PassChangeButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// StyledDivider copied from ReservationForm.tsx
const StyledDivider = scStyled.div`
  border-top:1px solid ${(p) => p.theme.colors.lightgray09};
  width: 100%; 
  margin:1rem 0;
  padding:0;
`;

const FormTitleHeader = scStyled.div`
  background-color: ${(p) => p.theme.colors.lightgray09};
  padding:0.5rem;
  padding-left:1rem;
  font-size:14px;
  font-weight:bold;
  text-align:left;
  display: flex;    
  justify-content: space-between; 
  align-items: center;
  position: absolute;
    top: 0;
    z-index: 10;
    border-bottom:1px solid #bbb;
    width:100%;
    padding-right:1rem;
`;
